import React from "react";
import { Empty, Spin } from "antd";
import {
  AlertCircle,
  BarChart2,
  MapPin,
  TrendingDown,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";

const numberFormatter = new Intl.NumberFormat("en-IN");

const InfoCard = ({ icon: Icon, label, value, color = "#004877" }) => (
  <div className="flex items-start gap-3 rounded-2xl bg-slate-50 px-4 py-3">
    <div className="mt-0.5 rounded-xl p-2" style={{ background: `${color}18` }}>
      <Icon size={16} style={{ color }} />
    </div>
    <div>
      <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">{label}</p>
      <p className="mt-0.5 text-sm font-semibold text-slate-800">{value || "—"}</p>
    </div>
  </div>
);

const SwotBlock = ({ label, value, color, bg, icon: Icon }) => (
  <div className="rounded-2xl p-3" style={{ background: bg }}>
    <div className="mb-1.5 flex items-center gap-2">
      <Icon size={13} style={{ color }} />
      <span className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color }}>
        {label}
      </span>
    </div>
    <p className="text-xs leading-relaxed !text-black">{value || "—"}</p>
  </div>
);

const CandidateSwot = ({ index, data }) => {
  if (
    !data.strength &&
    !data.weakness &&
    !data.opportunities &&
    !data.threat
  )
    return null;

  return (
    <div className="rounded-[20px] border border-slate-100 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#0F3B63] text-xs font-bold !text-white">
          {index}
        </div>
        <span className="text-sm font-semibold text-[#123865]">Candidate {index}</span>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <SwotBlock
          label="Strength"
          value={data.strength}
          color="#16a34a"
          bg="#f0fdf4"
          icon={TrendingUp}
        />
        <SwotBlock
          label="Weakness"
          value={data.weakness}
          color="#dc2626"
          bg="#fef2f2"
          icon={TrendingDown}
        />
        <SwotBlock
          label="Opportunities"
          value={data.opportunities}
          color="#2563eb"
          bg="#eff6ff"
          icon={Zap}
        />
        <SwotBlock
          label="Threat"
          value={data.threat}
          color="#d97706"
          bg="#fffbeb"
          icon={AlertCircle}
        />
      </div>
    </div>
  );
};

const QuantitativeSurveyRightPanel = ({ analysis, selectedWard, isLoading }) => {
  const candidates = analysis
    ? [
        {
          strength: analysis.candidate1Strength,
          weakness: analysis.candidate1Weakness,
          opportunities: analysis.candidate1Opportunities,
          threat: analysis.candidate1Threat,
        },
        {
          strength: analysis.candidate2Strength,
          weakness: analysis.candidate2Weakness,
          opportunities: analysis.candidate2Opportunities,
          threat: analysis.candidate2Threat,
        },
        {
          strength: analysis.candidate3Strength,
          weakness: analysis.candidate3Weakness,
          opportunities: analysis.candidate3Opportunities,
          threat: analysis.candidate3Threat,
        },
        {
          strength: analysis.candidate4Strength,
          weakness: analysis.candidate4Weakness,
          opportunities: analysis.candidate4Opportunities,
          threat: analysis.candidate4Threat,
        },
        {
          strength: analysis.candidate5Strength,
          weakness: analysis.candidate5Weakness,
          opportunities: analysis.candidate5Opportunities,
          threat: analysis.candidate5Threat,
        },
      ]
    : [];
      console.log("selectedWard",selectedWard)
      console.log("analysis",analysis)
  return (
    <div className="absolute top-[10%] right-4 z-[2200] w-[500px] max-w-[calc(100vw-2rem)] max-h-[82vh] overflow-y-auto rounded-[28px] border border-white/30 bg-white/70 p-4 shadow-[0_30px_80px_rgba(15,23,42,0.22)] backdrop-blur-xl">
      {/* Header */}
      <div className="mb-4 rounded-[24px] bg-[linear-gradient(135deg,#0F3B63_0%,#16608D_58%,#78C5E7_100%)] p-5 text-white">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] !text-white/70">
              Quantitative Survey
            </p>
            <h2 className="mt-2 text-2xl font-semibold">
              {selectedWard || "Select a Ward"}
            </h2>
            <p className="mt-2 text-sm !text-white/80">
              {selectedWard
                ? "Click any ward on the map or use the filter to explore."
                : "Click on a ward polygon or use the Ward filter on the left to load data."}
            </p>
          </div>
          <div className="rounded-2xl border border-white/20 bg-white/10 px-3 py-2 text-right">
            <p className="text-xs uppercase tracking-[0.2em] text-white/60">AC</p>
            <p className="mt-1 text-base font-semibold">{analysis?.ac || "—"}</p>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <Spin size="large" />
        </div>
      ) : !analysis ? (
        <div className="rounded-2xl bg-white p-10">
          <Empty
            description={
              selectedWard
                ? "No data available for this ward."
                : "Select a ward from the map or filter to view SWOT analysis."
            }
          />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Ward Info Cards */}
          {selectedWard && <div className="grid grid-cols-2 gap-3">
            <InfoCard
              icon={Users}
              label="Total Population"
              value={
                analysis.totalPopulation
              }
              color="#004877"
            />
            <InfoCard
              icon={MapPin}
              label="Polling Stations"
              value={
                analysis.totalPollingStations
                  ? numberFormatter.format(analysis.totalPollingStations)
                  : null
              }
              color="#16608D"
            />
            <InfoCard
              icon={BarChart2}
              label="Municipal Corporator"
              value={analysis.municipalCorporator}
              color="#0F3B63"
            />
            <InfoCard
              icon={AlertCircle}
              label="Issues"
              value={analysis.issues}
              color="#d97706"
            />
          </div>}

          {/* Candidate SWOT */}
          <div>
            <div className="mb-3">
              <h3 className="text-sm font-semibold text-[#123865]">
                Candidate SWOT Analysis
              </h3>
              <p className="text-xs text-slate-500">
                Strengths, Weaknesses, Opportunities & Threats per candidate.
              </p>
            </div>
            <div className="space-y-3">
              {candidates.map((c, i) => (
                <CandidateSwot key={i} index={i + 1} data={c} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuantitativeSurveyRightPanel;
